import {
    DatabaseType,
    GeographyType,
    GeometryPoint,
    GeometryType,
    InputPolygonGeometry,
    InputPolygonProperties,
    InputViewBox,
    PolygonProperties,
    SavedArea,
} from "@biggeo/bg-server-lib/studio";
import { WithRequiredProperty } from "@biggeo/bg-utils";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
    bbox,
    bboxClip,
    bboxPolygon,
    booleanOverlap,
    booleanWithin,
    buffer,
    difference,
    featureCollection,
    midpoint,
    point,
    polygon,
    union,
} from "@turf/turf";
import * as A from "fp-ts/Array";
import { pipe } from "fp-ts/lib/function";
import compact from "lodash/compact";
import filter from "lodash/filter";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import isUndefined from "lodash/isUndefined";
import some from "lodash/some";
import mapboxgl from "mapbox-gl";
import { match } from "ts-pattern";

import {
    MapContextDataset,
    MapContextPolygon,
    MapContextPolygonSource,
} from "../mapbox/context/types";
import { setPointPulsingDot } from "../mapbox/utils/data-layers-utils";
import { getLongLat } from "../mapbox/utils/filtered-data-hooks-utils";
import {
    MAP_TOOLTIP_ARROW_HEIGHT,
    MAP_TOOLTIP_HEIGHT,
    MAP_TOOLTIP_WIDTH,
} from "../mapbox/views/MapPromptPopup";
import { convertCoordsToPixels, getCircleVertices } from "./draw-modes-utils";
import { DataRspRows, PolygonFeature } from "./type";
import { isMultiPolygon, isPolygon, isPolygonFeature } from "./converters";
import { v4 as uuid } from "uuid";
import { Bbox } from "@mapbox/mapbox-gl-geocoder";
import isNil from "lodash/isNil";

// ██████╗ ██████╗  █████╗ ██╗    ██╗
// ██╔══██╗██╔══██╗██╔══██╗██║    ██║
// ██║  ██║██████╔╝███████║██║ █╗ ██║
// ██║  ██║██╔══██╗██╔══██║██║███╗██║
// ██████╔╝██║  ██║██║  ██║╚███╔███╔╝
// ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝ ╚══╝╚══╝

export const setDrawFeature = ({
    currentShapeId,
    feature,
    draw,
    isMapLoaded,
}: {
    currentShapeId?: string | number;
    feature:
        | GeoJSON.Feature<GeoJSON.Polygon>
        | GeoJSON.Feature<GeoJSON.MultiPolygon>
        | GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
    draw: React.MutableRefObject<MapboxDraw | null>;
    isMapLoaded: boolean;
}): GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[] => {
    if (isPolygon(feature)) {
        const features = getPolygonFeatureFromConflict({
            feature,
            currentShapeId,
            features: getMapFeatures(draw, isMapLoaded),
        });

        draw.current?.set({
            type: "FeatureCollection",
            features,
        });

        return features;
    }

    if (isMultiPolygon(feature)) {
        const features = getMultiPolygonFeatureFromConflict({
            feature,
            currentShapeId,
            features: getMapFeatures(draw, isMapLoaded),
        });

        draw.current?.set({
            type: "FeatureCollection",
            features,
        });

        return features;
    }

    return [];
};

export const getMapFeatures = (
    draw: React.MutableRefObject<MapboxDraw | null>,
    isMapLoaded: boolean
): GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[] => {
    if (!isMapLoaded) {
        return [];
    }

    const current = draw.current;

    if (isNil(current)) {
        return [];
    }

    // Double checking here again on `current` because Sentry throws this error even with the if statement above:
    // Cannot read properties of null (reading 'getAll')
    const features: GeoJSON.Feature<
        GeoJSON.Geometry,
        GeoJSON.GeoJsonProperties
    >[] = current ? current.getAll().features : [];

    if (features.length === 0) {
        return [];
    }

    return pipe(
        features,
        A.filter((feature) => {
            if (isPolygonFeature(feature)) {
                return (
                    feature.geometry.coordinates[0].length >= 3 &&
                    isArray(feature.geometry.coordinates[0])
                );
            }

            return !!feature;
        }),
        A.map((f) => ({
            ...f,
            properties: {
                ...f.properties,
                featureId: f.id,
            },
        })),
        compact
    );
};

export const getMapDrawnFeatures = (
    draw: React.MutableRefObject<MapboxDraw | null>,
    isMapLoaded: boolean
): MapContextPolygon[] => {
    const features = getMapFeatures(draw, isMapLoaded);

    return features.map((feature) => ({
        id: feature.id ? feature.id.toString() : uuid(),
        source: MapContextPolygonSource.drawnShape,
        feature,
        isSelected: feature.properties?.selected ?? false,
        isConflict: false,
    }));
};

//  ██████╗ ███████╗ ██████╗ ███╗   ███╗███████╗████████╗██████╗ ██╗   ██╗
// ██╔════╝ ██╔════╝██╔═══██╗████╗ ████║██╔════╝╚══██╔══╝██╔══██╗╚██╗ ██╔╝
// ██║  ███╗█████╗  ██║   ██║██╔████╔██║█████╗     ██║   ██████╔╝ ╚████╔╝
// ██║   ██║██╔══╝  ██║   ██║██║╚██╔╝██║██╔══╝     ██║   ██╔══██╗  ╚██╔╝
// ╚██████╔╝███████╗╚██████╔╝██║ ╚═╝ ██║███████╗   ██║   ██║  ██║   ██║
//  ╚═════╝ ╚══════╝ ╚═════╝ ╚═╝     ╚═╝╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝

export const closePolygon = (polygon: number[][]) => {
    const first = polygon[0];
    const last = polygon[polygon.length - 1];

    if (!first || !last) {
        return [];
    }

    if (first[0] === last[0] && first[1] === last[1]) {
        return polygon;
    }

    return [...polygon, first];
};

export const getInputGeometry = (
    feature: GeoJSON.Feature<GeoJSON.Geometry>
): InputPolygonGeometry => {
    const coordinates = isPolygonFeature(feature)
        ? feature.geometry.coordinates
        : [];
    const properties = feature.properties;

    return {
        type: GeometryType.Polygon,
        id: feature.id ? feature.id.toString() : undefined,
        coordinates,
        properties: properties
            ? {
                  name: properties.name || undefined,
                  fill:
                      properties["fill-color"] && properties["fill-opacity"]
                          ? {
                                color: properties["fill-color"],
                                opacity: properties["fill-opacity"],
                            }
                          : undefined,
                  stroke:
                      properties["stroke-color"] && properties["stroke-opacity"]
                          ? {
                                color: properties["stroke-color"] || undefined,
                                opacity:
                                    properties["stroke-opacity"] || undefined,
                            }
                          : undefined,
                  fkAreaId: properties.fkAreaId || undefined,
                  fkSavedAreaId: properties.fkSavedAreaId || undefined,
              }
            : undefined,
    };
};

/**
 * Determines the type of polygon based on its properties
 * @param polygon - The polygon feature to check
 * @returns "circle" if isCircle is true, "square" if isSquare is true, otherwise "polygon"
 */
export const getPolygonType = (
    polygon: PolygonFeature
): "circle" | "square" | "polygon" => {
    return polygon.properties?.isCircle
        ? "circle"
        : polygon.properties?.isSquare
          ? "square"
          : "polygon";
};

/**
 * Creates a buffered polygon from a GeoJSON polygon feature with a specified buffer distance
 * @param polygonFeature - The input GeoJSON polygon feature to be buffered
 * @param value - The buffer distance in kilometers
 * @param id - The identifier to be assigned to the resulting buffered polygon
 * @returns A new GeoJSON feature with the buffered polygon and assigned ID, or undefined if buffering fails
 */
export const createBufferedPolygon = (
    polygonFeature: GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>,
    value: number,
    id: string
):
    | WithRequiredProperty<
          GeoJSON.Feature<
              GeoJSON.Polygon | GeoJSON.MultiPolygon,
              GeoJSON.GeoJsonProperties
          >,
          "id"
      >
    | undefined => {
    const newBufferedPolygon = buffer(polygonFeature, value, {
        units: "kilometers",
    });

    if (!newBufferedPolygon) return;

    const newBufferedPolygonWithId = {
        ...newBufferedPolygon,
        id,
    };

    return newBufferedPolygonWithId;
};

export const getPolygonsThatOverlapOtherPolygons = (
    inner: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[],
    outer: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[]
): GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[] => {
    /*
        booleanOverlap is to catch the polygons that are partly inside other polygons.
        booleanWithin is to catch the ones that are completely inside.
    */
    return filter(outer, (outerPolygon) =>
        some(
            inner,
            (innerPolygon) =>
                booleanOverlap(outerPolygon, innerPolygon) ||
                booleanWithin(innerPolygon, outerPolygon)
        )
    );
};

export const getShapeProperties = (
    properties?: PolygonProperties | InputPolygonProperties
):
    | {
          name?: string;
          "fill-color": string | undefined;
          "stroke-color": string | undefined;
          "fill-opacity": number | undefined;
          "stroke-opacity": number | undefined;
          fkAreaId: string | undefined;
          fkSavedAreaId: string | undefined;
      }
    | undefined => {
    return properties
        ? {
              name: properties.name || undefined,
              "fill-color": properties.fill?.color || undefined,
              "stroke-color": properties.stroke?.color || undefined,
              "fill-opacity": properties.fill?.opacity || undefined,
              "stroke-opacity": properties.stroke?.opacity || undefined,
              fkAreaId: properties.fkAreaId || undefined,
              fkSavedAreaId: properties.fkSavedAreaId || undefined,
          }
        : undefined;
};

export const handleAntiMeridian = (dataPoints: readonly GeometryPoint[]) => {
    const longs = dataPoints.map((p) => {
        return p.longitude;
    });
    const minLong = Math.min(...longs);
    const maxLong = Math.max(...longs);

    const points = dataPoints.map((p) => {
        if (maxLong - minLong > 180 && p.longitude > 0) {
            return [p.longitude - 360, p.latitude];
        }
        return [p.longitude, p.latitude];
    });

    return points;
};

// ██████╗  ██████╗ ██╗   ██╗███╗   ██╗██████╗ ███████╗
// ██╔══██╗██╔═══██╗██║   ██║████╗  ██║██╔══██╗██╔════╝
// ██████╔╝██║   ██║██║   ██║██╔██╗ ██║██║  ██║███████╗
// ██╔══██╗██║   ██║██║   ██║██║╚██╗██║██║  ██║╚════██║
// ██████╔╝╚██████╔╝╚██████╔╝██║ ╚████║██████╔╝███████║
// ╚═════╝  ╚═════╝  ╚═════╝ ╚═╝  ╚═══╝╚═════╝ ╚══════╝

export const fitBounds = ({
    latBounds,
    lngBounds,
}: {
    latBounds: { min: number; max: number };
    lngBounds: { min: number; max: number };
}) => {
    if (lngBounds.max - lngBounds.min > 360) {
        return {
            latBounds: {
                min: latBounds.min,
                max: latBounds.max,
            },
            lngBounds: {
                min: Math.max(lngBounds.min, -180),
                max: Math.min(lngBounds.max, 180),
            },
        };
    }
    return {
        latBounds: {
            min: latBounds.min,
            max: latBounds.max,
        },
        lngBounds: {
            min: lngBounds.min,
            max: lngBounds.max,
        },
    };
};

export const getBounds = (bounds: mapboxgl.LngLatBounds): InputViewBox => {
    const maxLat = bounds._ne.lat;
    const minLat = bounds._sw.lat;
    const maxLng = bounds._ne.lng;
    const minLng = bounds._sw.lng;

    return fitBounds({
        latBounds: {
            min: minLat,
            max: maxLat,
        },
        lngBounds: {
            min: minLng,
            max: maxLng,
        },
    });
};

export const getFeatureBounds = (
    feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
): mapboxgl.LngLatBounds => {
    const [minLng, minLat, maxLng, maxLat] = bbox(feature);

    return new mapboxgl.LngLatBounds([minLng, minLat], [maxLng, maxLat]);
};

export const snapToView = ({
    features,
    map,
}: {
    readonly features: GeoJSON.Feature<
        GeoJSON.Geometry,
        GeoJSON.GeoJsonProperties
    >[];
    readonly map: React.MutableRefObject<mapboxgl.Map | null>;
}) => {
    if (!isEmpty(features) && map.current) {
        const convertedPolygons = pipe(
            features,
            A.map((feature) => {
                const coordinates = isPolygonFeature(feature)
                    ? feature.geometry.coordinates[0]
                    : [];

                const properties = feature.properties;

                // const polypoints = pipe(
                //     coordinates,
                //     A.map((coordinate) => ({
                //         latitude: coordinate[1],
                //         longitude: coordinate[0],
                //     })),
                //     A.filterWithIndex(
                //         (i: number) => i !== coordinates.length - 1
                //     )
                // )
                // const points = polypoints.map((p) => {
                //     return [p.longitude, p.latitude];
                // });
                return coordinates.length >= 4
                    ? polygon([coordinates], {
                          ...getShapeProperties(properties || undefined),
                      })
                    : undefined;
            }),
            compact
        );

        if (!isEmpty(convertedPolygons) && convertedPolygons.length > 0) {
            const bBox = bbox(convertedPolygons[0]);

            const sw = new mapboxgl.LngLat(bBox[0], bBox[1]);
            const ne = new mapboxgl.LngLat(bBox[2], bBox[3]);

            // biome-ignore lint/correctness/noInnerDeclarations: <explanation>
            // biome-ignore lint/style/noVar: <explanation>
            var llb = new mapboxgl.LngLatBounds(sw, ne);

            // biome-ignore lint/complexity/noForEach: <explanation>
            convertedPolygons.forEach((convertedPolygon) => {
                const bboxCoord = bbox(
                    convertedPolygon
                ) as mapboxgl.LngLatBoundsLike;
                llb.extend(bboxCoord);
            });

            map.current.fitBounds(llb, { padding: 50 });
        }
    }
};

//  ██████╗ ██████╗  ██████╗ ██████╗ ██████╗ ██╗███╗   ██╗ █████╗ ████████╗███████╗███████╗
// ██╔════╝██╔═══██╗██╔═══██╗██╔══██╗██╔══██╗██║████╗  ██║██╔══██╗╚══██╔══╝██╔════╝██╔════╝
// ██║     ██║   ██║██║   ██║██████╔╝██║  ██║██║██╔██╗ ██║███████║   ██║   █████╗  ███████╗
// ██║     ██║   ██║██║   ██║██╔══██╗██║  ██║██║██║╚██╗██║██╔══██║   ██║   ██╔══╝  ╚════██║
// ╚██████╗╚██████╔╝╚██████╔╝██║  ██║██████╔╝██║██║ ╚████║██║  ██║   ██║   ███████╗███████║
//  ╚═════╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═╝╚═╝  ╚═══╝╚═╝  ╚═╝   ╚═╝   ╚══════╝╚══════╝

const findHighestPoint = (coords: GeoJSON.Position[]): GeoJSON.Position => {
    return coords.reduce((prev, curr) => (curr[1] > prev[1] ? curr : prev));
};

/**
 * Gets the highest coordinate of a polygon feature
 * @param polygon - The polygon feature to get the midpoint for
 * @returns GeoJSON Position as [longitude, latitude]
 */
export const getPolygonHighestPoint = (
    polygon?: PolygonFeature
): GeoJSON.Position => {
    if (!polygon) {
        return [0, 0];
    }

    const type = getPolygonType(polygon);
    const geometry = polygon.geometry;

    return match(type)
        .with("circle", () => {
            const vertices = getCircleVertices(polygon);
            const coords = vertices.map((v) => v.geometry.coordinates);
            return findHighestPoint(coords);
        })
        .with("square", () => {
            const coords = geometry.coordinates[0];
            const corners = coords.slice(0, 4); // Skip last repeated vertex
            return findHighestPoint(corners);
        })
        .with("polygon", () => {
            const coords = geometry.coordinates[0];
            const vertices = coords.slice(0, -1); // Remove last repeated vertex
            return findHighestPoint(vertices);
        })
        .exhaustive();
};

export const getPolygonTooltipPosition = ({
    polygon,
    map,
    position = "top",
}: {
    polygon?: PolygonFeature;
    map: mapboxgl.Map;
    position?: "top" | "bottom";
}): {
    top: number;
    left: number;
} => {
    if (!polygon) {
        return {
            top: 0,
            left: 0,
        };
    }

    const geometry = polygon?.geometry;
    const type = getPolygonType(polygon);

    const coordinates = match(type)
        .with("circle", () => {
            const vertices = getCircleVertices(polygon);
            const topVertex = vertices[0].geometry.coordinates;
            const bottomVertex = vertices[3].geometry.coordinates;

            const vertex = match(position)
                .with("top", () => topVertex)
                .with("bottom", () => bottomVertex)
                .exhaustive();

            return convertCoordsToPixels(vertex, map);
        })
        .with("square", () => {
            const coord1 = geometry.coordinates[0][2]; // Top left
            const coord2 = geometry.coordinates[0][3]; // Top right

            const coord3 = geometry.coordinates[0][1]; // Bottom left
            const coord4 = geometry.coordinates[0][0]; // Bottom right

            const midpointCoord = match(position)
                .with("top", () => midpoint(point(coord1), point(coord2)))
                .with("bottom", () => midpoint(point(coord3), point(coord4)))
                .exhaustive();

            return convertCoordsToPixels(
                midpointCoord.geometry.coordinates,
                map
            );
        })
        .with("polygon", () => {
            const topVertex = geometry.coordinates[0][0];
            const bottomVertex = geometry.coordinates[0][1];

            const vertex = match(position)
                .with("top", () => topVertex)
                .with("bottom", () => bottomVertex)
                .exhaustive();

            return convertCoordsToPixels(vertex, map);
        })
        .exhaustive();

    const top = match(position)
        .with(
            "top",
            () =>
                coordinates.top -
                MAP_TOOLTIP_HEIGHT -
                MAP_TOOLTIP_ARROW_HEIGHT -
                10
        )
        .with("bottom", () => coordinates.top + 10)
        .exhaustive();

    const left = match(position)
        .with("top", () => coordinates.left - MAP_TOOLTIP_WIDTH / 2 - 10)
        .with("bottom", () => coordinates.left)
        .exhaustive();

    return { top, left };
};

export const getPolygonsFromSavedAreas = (
    savedAreas: readonly SavedArea[]
): GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[] => {
    return savedAreas.flatMap((savedArea) =>
        savedArea.geometries.geometries.flatMap((geometry) => ({
            id: geometry.properties?.fkSavedAreaId ?? savedArea.id,
            type: "Feature",
            properties:
                getShapeProperties(
                    geometry.properties
                        ? {
                              ...geometry.properties,
                              name: geometry.properties?.name ?? savedArea.name,
                              fkSavedAreaId:
                                  geometry.properties?.fkSavedAreaId ??
                                  savedArea.id,
                              fkAreaId:
                                  geometry.properties?.fkAreaId ??
                                  savedArea.fkAreaId,
                          }
                        : {
                              name: savedArea.name,
                              fkSavedAreaId: savedArea.id,
                              fkAreaId: savedArea.fkAreaId,
                          }
                ) || {},
            geometry: {
                type: "Polygon",
                coordinates: geometry.coordinates as GeoJSON.Position[][],
            },
        }))
    );
};

export const getAreaOutsideBoundaries = (
    boundaries: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[]
): GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | undefined => {
    if (boundaries.length === 0) {
        return undefined;
    }

    const unionPolygon = boundaries.reduce((acc, polygon) => {
        const collection = featureCollection([
            acc,
            polygon,
        ]) as GeoJSON.FeatureCollection<
            GeoJSON.Polygon | GeoJSON.MultiPolygon,
            GeoJSON.GeoJsonProperties
        >;
        const output = union(collection);

        return output ? output : acc;
    }, boundaries[0]);

    // Bbox of the world
    const bbox: GeoJSON.BBox = [-180, -90, 180, 90];
    const bboxPolygonCoord = bboxPolygon(bbox);

    const collection = featureCollection([
        bboxPolygonCoord,
        unionPolygon,
    ]) as GeoJSON.FeatureCollection<
        GeoJSON.Polygon | GeoJSON.MultiPolygon,
        GeoJSON.GeoJsonProperties
    >;

    const outsideArea = difference(collection);

    return outsideArea
        ? outsideArea
        : {
              type: "Feature",
              geometry: {
                  type: "Polygon",
                  coordinates: [],
              },
              properties: {},
          };
};

export const areSquareCoordinatesCorrect = (
    coordinates: GeoJSON.Position[][]
): boolean => {
    const isSquare = coordinates[0].length === 5;

    if (!isSquare) {
        return false;
    }

    const first = coordinates[0][0];
    const second = coordinates[0][1];
    const third = coordinates[0][2];
    const fourth = coordinates[0][3];
    const fifth = coordinates[0][4];

    if (!isEqual(first, fifth)) {
        return false;
    }

    if (!isEqual(second[1], first[1])) {
        return false;
    }

    if (!isEqual(third[1], fourth[1])) {
        return false;
    }

    if (!isEqual(fourth[0], fifth[0])) {
        return false;
    }

    return true;
};

export const fixSquareCoordinates = (coordinates: GeoJSON.Position[][]) => {
    const fourthCoordinates = coordinates[0][3];
    const secondCoordinates = coordinates[0][1];

    return [
        pipe(
            coordinates[0],
            A.mapWithIndex((index, c) => {
                if (index === 1) {
                    return fourthCoordinates;
                }

                if (index === 3) {
                    return secondCoordinates;
                }

                return c;
            })
        ),
    ];
};

export const getPolygonFeatureFromConflict = ({
    currentShapeId,
    feature,
    features,
}: {
    currentShapeId?: string | number;
    feature: GeoJSON.Feature<GeoJSON.Polygon>;
    features: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[];
}) => {
    return pipe(
        features,
        A.map((f) => {
            const isCircleProperty = !isUndefined(f.properties?.isCircle);
            const isSquareProperty = !isUndefined(f.properties?.isSquare);

            const coordinates = feature.geometry.coordinates;

            const isNotSquareAnymore =
                isSquareProperty && feature.geometry.coordinates[0].length > 5;

            // When an intersection is calculated, it sometimes messes up the
            // coordinates order, which is important for a square.
            const isCorrectSquare = areSquareCoordinatesCorrect(coordinates);

            const updatedCoordinates = isSquareProperty
                ? isCorrectSquare || isNotSquareAnymore
                    ? coordinates
                    : fixSquareCoordinates(coordinates)
                : coordinates;

            return f.id === currentShapeId
                ? ({
                      ...f,
                      geometry: {
                          ...f.geometry,
                          coordinates: updatedCoordinates,
                      },
                      properties: {
                          ...f.properties,
                          ...(isCircleProperty && {
                              isCircle: false,
                              wasCircle: true,
                          }),
                          ...(isNotSquareAnymore && {
                              isSquare: false,
                              wasSquare: true,
                          }),
                      },
                  } as GeoJSON.Feature<
                      GeoJSON.Geometry,
                      GeoJSON.GeoJsonProperties
                  >)
                : f;
        })
    );
};

export const getMultiPolygonFeatureFromConflict = ({
    currentShapeId,
    feature,
    features,
}: {
    currentShapeId?: string | number;
    feature: GeoJSON.Feature<GeoJSON.MultiPolygon>;
    features: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[];
}) => {
    return pipe(
        features,
        A.concat(
            pipe(
                feature.geometry.coordinates,
                A.mapWithIndex((index, coordinates) => {
                    const isCircleProperty = !isUndefined(
                        feature.properties?.isCircle
                    );
                    const isSquareProperty = !isUndefined(
                        feature.properties?.isSquare
                    );

                    const isNotSquareAnymore =
                        isSquareProperty && coordinates[0].length > 5;

                    // When an intersection is calculated, it sometimes messes up the
                    // coordinates order, which is important for a square.
                    const isCorrectSquare =
                        areSquareCoordinatesCorrect(coordinates);

                    const updatedCoordinates = isSquareProperty
                        ? isCorrectSquare || isNotSquareAnymore
                            ? coordinates
                            : fixSquareCoordinates(coordinates)
                        : coordinates;

                    return {
                        id:
                            index !== 0
                                ? `${currentShapeId?.toString().slice(0, -1)}${index}`
                                : currentShapeId,
                        type: "Feature",
                        properties: {
                            ...feature.properties,
                            ...(isCircleProperty && {
                                isCircle: false,
                                wasCircle: true,
                            }),
                            ...(isNotSquareAnymore && {
                                isSquare: false,
                                wasSquare: true,
                            }),
                        },
                        geometry: {
                            type: MapboxDraw.constants.geojsonTypes.POLYGON,
                            coordinates: updatedCoordinates,
                        },
                    };
                })
            ) as GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[]
        )
    );
};

//  ██████╗ ███████╗ ██████╗  ██████╗ ██████╗ ██████╗ ███████╗██████╗
// ██╔════╝ ██╔════╝██╔═══██╗██╔════╝██╔═══██╗██╔══██╗██╔════╝██╔══██╗
// ██║  ███╗█████╗  ██║   ██║██║     ██║   ██║██║  ██║█████╗  ██████╔╝
// ██║   ██║██╔══╝  ██║   ██║██║     ██║   ██║██║  ██║██╔══╝  ██╔══██╗
// ╚██████╔╝███████╗╚██████╔╝╚██████╗╚██████╔╝██████╔╝███████╗██║  ██║
//  ╚═════╝ ╚══════╝ ╚═════╝  ╚═════╝ ╚═════╝ ╚═════╝ ╚══════╝╚═╝  ╚═╝

export const coordinateFeature = (lng: number, lat: number) => {
    return {
        center: [lng, lat],
        geometry: {
            type: "Point",
            coordinates: [lng, lat],
        },
        place_name: `Lat: ${lat} Lng: ${lng}`,
        place_type: ["coordinate"],
        properties: {},
        type: "Feature",
    } as MapboxGeocoder.Result;
};

export const coordinatesGeocoder = (query: string): MapboxGeocoder.Result[] => {
    // Match anything which looks like
    // decimal degrees coordinate pair.
    const matches = query.match(
        /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
    );
    if (!matches) {
        return [];
    }

    const coord1 = Number(matches[1]);
    const coord2 = Number(matches[2]);
    const geocodes = [];

    if (coord1 < -90 || coord1 > 90) {
        // must be lng, lat
        geocodes.push(coordinateFeature(coord1, coord2));
    }

    if (coord2 < -90 || coord2 > 90) {
        // must be lat, lng
        geocodes.push(coordinateFeature(coord2, coord1));
    }

    if (geocodes.length === 0) {
        // else could be either lng, lat or lat, lng
        geocodes.push(coordinateFeature(coord1, coord2));
        geocodes.push(coordinateFeature(coord2, coord1));
    }

    return geocodes;
};

export const getPointFeatures = (input: {
    datasetId: string;
    rows: DataRspRows;
    tableId?: string;
    geographyColumn: string;
    geographyType?: GeographyType | null;
    type: DatabaseType;
}): GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[] => {
    const { rows, geographyColumn, tableId, datasetId, type, geographyType } =
        input;

    const isPoint = type === DatabaseType.point;

    return pipe(
        rows,
        A.map((row) => {
            const id = tableId ? row[tableId].toString() : undefined;

            if (isPoint) {
                const { latitude, longitude } = getLongLat({
                    geographyColumn,
                    row,
                    geographyType,
                });

                const pointCoord =
                    latitude && longitude ? [longitude, latitude] : [0, 0];

                return point(pointCoord, {
                    label: `Point - ${id}`,
                    count: 1,
                    formattedId: id,
                    id: JSON.stringify({
                        uint64: Number(id),
                    }),
                    databaseId: datasetId,
                });
            }

            if (!isPoint) {
                const latLongProp = geographyColumn;
                const latLong: GeoJSON.Geometry = row[latLongProp];

                return {
                    type: MapboxDraw.constants.geojsonTypes.FEATURE,
                    geometry: latLong,
                    properties: {
                        label: `Polygon/MultiPolygon/LineString - ${id}`,
                        count: 1,
                        formattedId: id,
                        id: JSON.stringify({
                            uint64: Number(id),
                        }),
                        databaseId: datasetId,
                    },
                };
            }
        }),
        compact
    );
};

export const setSelectedRowPulsingDot = (i: {
    action: "add" | "remove" | "update";
    datasets: MapContextDataset[];
    polygons: MapContextPolygon[];
    map: React.MutableRefObject<mapboxgl.Map | null>;
    isMapLoaded: boolean;
}) => {
    const { action, polygons, datasets, map, isMapLoaded } = i;

    const boundaries = polygons
        .filter((p) => p.source === MapContextPolygonSource.boundary)
        .map((p) => p.feature);

    const outsideBoundariesArea = getAreaOutsideBoundaries(boundaries);

    const rows = pipe(
        datasets,
        A.filter((d) => isEqual(d.isSelected, true)),
        A.flatMap((d) => d.selectedRows)
    );

    pipe(
        datasets,
        A.filter((d) => isEqual(d.isSelected, true)),
        A.map((dataset) => {
            if (map.current && isMapLoaded) {
                const features = isEmpty(rows)
                    ? []
                    : getPointFeatures({
                          datasetId: dataset.dataSource.id,
                          tableId: dataset.dataSource.tableId || undefined,
                          geographyColumn:
                              dataset.dataSource.geographyColumn ?? "",
                          rows: pipe(
                              dataset.selectedRows,
                              A.map(({ row }) => JSON.parse(row))
                          ),
                          type: dataset.dataSource.type ?? DatabaseType.point,
                          geographyType: dataset.geographyType,
                      });

                // When a boundary is selected, we check if the selected point is inside it.
                // If inside, we add it. If outside, we remove it.
                const featuresInsideBoundaries = outsideBoundariesArea
                    ? features.filter(
                          (feature) =>
                              !booleanWithin(feature, outsideBoundariesArea)
                      )
                    : features;

                setPointPulsingDot({
                    datasetId: dataset.dataSource.id,
                    action: action,
                    map,
                    isMapLoaded,
                    color:
                        dataset.mapTemplateDataset?.styles?.fill?.color ||
                        undefined,
                    features: featuresInsideBoundaries,
                });
            }
        })
    );
};
